/**
 * usePlanLimits — hook that exposes the current user's plan limits and
 * actual usage counts fetched from Supabase.
 *
 * Usage:
 *   const { limits, usage, loading, canCreateForm, formUsagePct } = usePlanLimits();
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getPlanLimits, formatLimit, normalizePlanKey, PLAN_DISPLAY, resolveConfiguredLimits, resolveFeatureAccess } from '../config/planFeatures';

export const usePlanLimits = () => {
  const { user, userProfile, isAdmin, isOwner, loading: authLoading } = useAuth();
  const [usage, setUsage] = useState({ forms: 0, submissions: 0, teamMembers: 1 });
  const [loading, setLoading] = useState(true);
  // Live Stripe subscription info (from the stripe_subscriptions cache table).
  // null while loading or when the user has no active subscription.
  const [subscription, setSubscription] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState(false);
  const [featureEntitlements, setFeatureEntitlements] = useState(null);
  const [entitlementsReady, setEntitlementsReady] = useState(false);
  const [configuredLimits, setConfiguredLimits] = useState(null);

  const planType = normalizePlanKey(userProfile?.plan_type || userProfile?.tier || 'free');
  const fallbackLimits = getPlanLimits(planType);
  const limits = resolveConfiguredLimits(configuredLimits, fallbackLimits);

  // Display metadata for the user's plan (name + list price), used as a
  // fallback when there is no Stripe subscription row for this user.
  const planDisplay = PLAN_DISPLAY[planType] || null;

  // Billing info for the "Current Plan" header. Sources, in order:
  //  1. Live Stripe subscription cache row — used verbatim; null fields stay
  //     null (rendered as a dash), never patched with static config.
  //  2. No subscription row (and no error) — plan name/price derived from the
  //     user's profile plan_type, per the profile-based source of truth.
  //  3. Query error — everything null so the UI shows dashes instead of
  //     potentially wrong static values.
  const billing = billingError
    ? {
        planName: null, price: null, currency: 'usd', interval: 'monthly',
        nextBillingDate: null, endsAt: null, cancelAtPeriodEnd: false, status: null,
      }
    : subscription
      ? {
          planName:        subscription.plan ?? null,
          price:           subscription.amount ?? null,
          currency:        subscription.currency ?? 'usd',
          interval:        subscription.interval ?? 'monthly',
          // Null when unknown OR when the subscription will not renew
          // (cancel_at_period_end) — callers should render a dash.
          nextBillingDate: subscription.next_billing_date ?? null,
          // When cancelling at period end, the date the subscription ends.
          endsAt:          subscription.cancel_at_period_end
            ? (subscription.current_period_end ?? null)
            : null,
          cancelAtPeriodEnd: subscription.cancel_at_period_end ?? false,
          status:          subscription.status ?? null,
        }
      : {
          planName:        planDisplay?.label ?? null,
          price:           planDisplay?.price ?? null,
          currency:        'usd',
          interval:        'monthly',
          nextBillingDate: null,
          endsAt:          null,
          cancelAtPeriodEnd: false,
          status:          null,
        };

  const fetchSubscription = useCallback(async () => {
    // Reset state so a user switch never shows the previous user's data.
    setSubscription(null);
    setBillingError(false);
    if (!user?.email) {
      setBillingLoading(false);
      return;
    }
    setBillingLoading(true);
    try {
      const { data, error } = await supabase
        .from('stripe_subscriptions')
        .select('plan, amount, currency, interval, status, next_billing_date, current_period_end, cancel_at_period_end')
        .eq('customer_email', user.email)
        .in('status', ['active', 'trialing', 'past_due'])
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[usePlanLimits] subscription fetch error:', error);
        setBillingError(true);
      } else if (data) {
        setSubscription({
          ...data,
          // Only backfill from current_period_end when the subscription will
          // actually renew — a cancelling subscription has no next billing.
          next_billing_date: data.cancel_at_period_end
            ? null
            : (data.next_billing_date || data.current_period_end || null),
        });
      }
    } catch (err) {
      console.error('[usePlanLimits] fetchSubscription failed:', err);
      setBillingError(true);
    } finally {
      setBillingLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Tier-level entitlements allow the pricing configurator to control known
  // feature gates without redeploying the client. A missing column/row simply
  // falls back to the shared catalog while the migration is being rolled out.
  useEffect(() => {
    let cancelled = false;
    const fetchFeatureEntitlements = async () => {
      setFeatureEntitlements(null);
      setEntitlementsReady(false);
      setConfiguredLimits(null);
      if (!user || authLoading) return;
      const { data, error } = await supabase
        .from('pricing_tiers')
        .select('*');
      const tier = data?.find((candidate) =>
        normalizePlanKey(candidate?.plan_key || candidate?.tier_name) === planType
      );
      if (cancelled) return;
      if (error) {
        console.error('[usePlanLimits] feature entitlement fetch error:', error);
        setFeatureEntitlements({});
        setEntitlementsReady(true);
        return;
      }
      setFeatureEntitlements(
        tier?.feature_entitlements && typeof tier.feature_entitlements === 'object'
          ? tier.feature_entitlements
          : {}
      );
      setConfiguredLimits(tier || null);
      setEntitlementsReady(true);
    };
    fetchFeatureEntitlements().catch((error) => {
      if (!cancelled) {
        console.error('[usePlanLimits] entitlement fetch failed:', error);
        setFeatureEntitlements({});
        setEntitlementsReady(true);
      }
    });
    return () => { cancelled = true; };
  }, [planType, user, authLoading]);

  const fetchUsage = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    try {
      const billingWindowStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const [formsResult, subsResult] = await Promise.all([
        supabase
          .from('forms')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .neq('status', 'deleted'),
        // Count submissions across all of the user's forms via the foreign-key join.
        // form_submissions does not carry a user_id column directly; ownership is
        // established through forms.user_id, so we filter via the inner join.
        supabase
          .from('form_submissions')
          .select('id, forms!inner(user_id)', { count: 'exact', head: true })
          .eq('forms.user_id', user.id)
          .gte('created_at', billingWindowStart),
      ]);

      if (formsResult.error) {
        console.error('[usePlanLimits] forms count error:', formsResult.error);
      }
      if (subsResult.error) {
        console.error('[usePlanLimits] submissions count error:', subsResult.error);
      }

      setUsage({
        forms:       formsResult.count ?? 0,
        submissions: subsResult.count ?? 0,
        teamMembers: 1, // Extended when team-member management is implemented
      });
    } catch (err) {
      console.error('[usePlanLimits] fetchUsage failed:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // ── Derived helpers ─────────────────────────────────────────────────────────

  const canCreateForm = () => {
    if (isAdmin || isOwner) return true;
    if (limits.maxForms === -1) return true;
    return usage.forms < limits.maxForms;
  };

  const canReceiveSubmission = () => {
    if (isAdmin || isOwner) return true;
    if (limits.maxSubmissions === -1) return true;
    return usage.submissions < limits.maxSubmissions;
  };

  const formUsagePct = () => {
    if (limits.maxForms === -1) return 0;
    return Math.min((usage.forms / limits.maxForms) * 100, 100);
  };

  const submissionUsagePct = () => {
    if (limits.maxSubmissions === -1) return 0;
    return Math.min((usage.submissions / limits.maxSubmissions) * 100, 100);
  };

  const formLimitLabel = () =>
    limits.maxForms === -1
      ? `${usage.forms} forms`
      : `${usage.forms} / ${formatLimit(limits.maxForms)} forms`;

  const submissionLimitLabel = () =>
    limits.maxSubmissions === -1
      ? `${usage.submissions.toLocaleString()} submissions this month`
      : `${usage.submissions.toLocaleString()} / ${formatLimit(limits.maxSubmissions)} submissions`;

  const isAtFormLimit    = () => limits.maxForms       !== -1 && usage.forms       >= limits.maxForms;
  const isNearFormLimit  = () => limits.maxForms       !== -1 && formUsagePct()       >= 80;
  const isAtSubLimit     = () => limits.maxSubmissions !== -1 && usage.submissions >= limits.maxSubmissions;
  const isNearSubLimit   = () => limits.maxSubmissions !== -1 && submissionUsagePct() >= 80;

  const getFeatureAccess = (minPlan, featureKey) =>
    resolveFeatureAccess({
      featureKey,
      minPlan,
      planType,
      accountFeatures: userProfile?.account_features,
      featureEntitlements,
      entitlementsReady,
      isAdmin,
      isOwner,
    });
  const hasFeatureAccess = (minPlan, featureKey) => getFeatureAccess(minPlan, featureKey).allowed;

  return {
    planType,
    limits,
    usage,
    loading,
    billing,
    billingLoading,
    entitlementsReady,
    refetch: fetchUsage,
    // Checks
    canCreateForm,
    canReceiveSubmission,
    isAtFormLimit,
    isNearFormLimit,
    isAtSubLimit,
    isNearSubLimit,
    hasFeatureAccess,
    getFeatureAccess,
    // Display helpers
    formUsagePct,
    submissionUsagePct,
    formLimitLabel,
    submissionLimitLabel,
  };
};
